import {
  ArrowDownTrayIcon,
  DocumentIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';

import { useGetBlobsQuery } from '@/services/api/blobService';
import { DiscriminatedBlobCreatedEvent } from '@/services/api/generated/matterChangesService';

import {
  ActivityActions,
  ActivityBody,
  ActivityDescription,
  ActivityIcon,
  ActivityItem,
  ActivityLink,
  ActivityTitle,
} from '../activity-feed-v2';
import { getUserFromEvent } from '../activity-feed-v2/ActivityDiffList';

export interface BlobCreatedEventProps {
  event: DiscriminatedBlobCreatedEvent;
}

export const BlobCreatedEvent: React.FC<BlobCreatedEventProps> = ({
  event,
}) => {
  const { blob } = event.payload;
  const user = getUserFromEvent(event);
  const matterId = event.matter_id;
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [blobExists, setBlobExists] = useState(true);

  const { data: blobs } = useGetBlobsQuery(matterId);

  useEffect(() => {
    if (blobs) {
      const currentBlob = blobs.find((b) => b.id === blob.id);
      if (currentBlob) {
        setDownloadUrl(currentBlob.download_url);
        setBlobExists(true);
      } else {
        setDownloadUrl(null);
        setBlobExists(false);
      }
    }
  }, [blobs, blob.id]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleDownload = () => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    }
  };

  const icon = (
    <ActivityIcon
      icon={
        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100">
          <DocumentIcon className="h-4 w-4 text-gray-600" />
        </div>
      }
    />
  );

  const body = (
    <ActivityBody className="flex flex-row gap-2">
      <ActivityTitle>{blob.name}</ActivityTitle>
      <ActivityDescription>
        <div className="text-xs text-gray-500">
          • {formatFileSize(blob.file_size)}
        </div>
      </ActivityDescription>
    </ActivityBody>
  );

  const actions = (
    <ActivityActions>
      <ActivityLink
        onClick={handleDownload}
        icon={<ArrowDownTrayIcon className="h-4 w-4" />}
        className={!blobExists ? 'cursor-not-allowed opacity-50' : ''}
      >
        {blobExists ? 'Download' : 'Download (Deleted)'}
      </ActivityLink>
      <ActivityLink
        href={`/matters/${matterId}/documents`}
        icon={<FolderIcon className="h-4 w-4" />}
      >
        Go to Documents
      </ActivityLink>
    </ActivityActions>
  );

  return (
    <ActivityItem
      icon={icon}
      title="File Uploaded"
      timestamp={new Date(event.occurred_at ?? new Date())}
      user={user}
      body={body}
      actions={actions}
    />
  );
};
